/**
 * SequenceFormFields — input grid for SequenceEditFormBody. Split out so the
 * containing form file stays under the 200-line CLAUDE.md budget.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D3 — Sequence UI.
 */
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import type { SqlEngine } from '../../../../sql-grammar/model';
import type { SequenceFormValues } from './sequenceFormHelpers';

/**
 * One shared treatment for the two fields ALTER cannot change (name, data
 * type). Both stay `readOnly` rather than `disabled` so the value is still
 * selectable/copyable, and both get the same muted, not-editable styling —
 * previously the name looked fully editable while the data type was greyed.
 */
const READ_ONLY_FIELD_CLASS = 'bg-muted/50 text-muted-foreground cursor-not-allowed';

/** Radix Select reserves `''`, so the "unset" option needs a sentinel value. */
const CYCLE_UNSET = '__unset__' as const;

export interface SequenceFormFieldsProps {
  readonly engine: SqlEngine;
  readonly mode: 'create' | 'alter';
  readonly values: SequenceFormValues;
  readonly onChange: <K extends keyof SequenceFormValues>(
    key: K,
    value: SequenceFormValues[K],
  ) => void;
  readonly testId: string;
}

export function SequenceFormFields(props: SequenceFormFieldsProps) {
  const { engine, mode, values, onChange, testId } = props;
  const showDataType = engine !== 'oracle';
  const showStart = mode === 'create';
  const isAlter = mode === 'alter';

  return (
    <>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-name`} className="text-xs">
          Sequence name <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${testId}-name`}
          data-testid={`${testId}-name`}
          value={values.name}
          onChange={(e) => onChange('name', e.target.value)}
          placeholder="my_sequence"
          className={`h-8 font-mono text-xs${isAlter ? ` ${READ_ONLY_FIELD_CLASS}` : ''}`}
          readOnly={isAlter}
        />
      </div>

      {showDataType ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-dataType`} className="text-xs">
            Data type (optional)
          </Label>
          <Input
            id={`${testId}-dataType`}
            data-testid={`${testId}-dataType`}
            value={values.dataType ?? ''}
            onChange={(e) => onChange('dataType', e.target.value)}
            placeholder="BIGINT"
            className={`h-8 font-mono text-xs${isAlter ? ` ${READ_ONLY_FIELD_CLASS}` : ''}`}
            readOnly={isAlter}
          />
        </div>
      ) : null}

      {showStart ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-start`} className="text-xs">
            Start with
          </Label>
          <Input
            id={`${testId}-start`}
            data-testid={`${testId}-start`}
            value={values.startWith ?? ''}
            onChange={(e) => onChange('startWith', e.target.value)}
            placeholder="1"
            className="h-8 font-mono text-xs"
          />
        </div>
      ) : null}

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-increment`} className="text-xs">
          Increment by
        </Label>
        <Input
          id={`${testId}-increment`}
          data-testid={`${testId}-increment`}
          value={values.incrementBy ?? ''}
          onChange={(e) => onChange('incrementBy', e.target.value)}
          placeholder="1"
          className="h-8 font-mono text-xs"
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-min`} className="text-xs">
            Min value
          </Label>
          <Input
            id={`${testId}-min`}
            data-testid={`${testId}-min`}
            value={values.minValue ?? ''}
            onChange={(e) => onChange('minValue', e.target.value)}
            placeholder="NONE = no min"
            className="h-8 font-mono text-xs"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-max`} className="text-xs">
            Max value
          </Label>
          <Input
            id={`${testId}-max`}
            data-testid={`${testId}-max`}
            value={values.maxValue ?? ''}
            onChange={(e) => onChange('maxValue', e.target.value)}
            placeholder="NONE = no max"
            className="h-8 font-mono text-xs"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-cycle`} className="text-xs">
            Cycle
          </Label>
          <Select
            value={values.cycle === undefined || values.cycle === '' ? CYCLE_UNSET : values.cycle}
            onValueChange={(v) => onChange('cycle', v === CYCLE_UNSET ? '' : v)}
          >
            <SelectTrigger
              id={`${testId}-cycle`}
              data-testid={`${testId}-cycle`}
              className="h-8 font-mono text-xs"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={CYCLE_UNSET}>
                {mode === 'create' ? 'Engine default (NO CYCLE)' : '(no change)'}
              </SelectItem>
              <SelectItem value="YES">CYCLE</SelectItem>
              <SelectItem value="NO">NO CYCLE</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-cache`} className="text-xs">
            Cache
          </Label>
          <Input
            id={`${testId}-cache`}
            data-testid={`${testId}-cache`}
            value={values.cache ?? ''}
            onChange={(e) => onChange('cache', e.target.value)}
            placeholder="20 or NONE"
            className="h-8 font-mono text-xs"
          />
        </div>
      </div>
    </>
  );
}
